import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import { useNavigate } from 'react-router-dom'
import TrialBanner from '../components/TrialBanner'

export default function Quotes({ session }) {
  const [quotes, setQuotes] = useState([])
  const [customers, setCustomers] = useState([])
  const [company, setCompany] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [sortBy, setSortBy] = useState('newest')
  const navigate = useNavigate()

  const defaultItems = [
    { description: 'Labour', quantity: 1, unit_price: 0, total: 0 },
  ]

  const [form, setForm] = useState({
    customer_id: '',
    reference: '',
    valid_until: '',
    notes: '',
    payment_terms: '',
    terms_conditions: '',
    vat_enabled: true,
    vat_rate: 20,
    items: defaultItems,
  })

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      setCompanyId(profile.company_id)
      const [{ data: q }, { data: c }, { data: co }] = await Promise.all([
        supabase.from('quotes').select(`*, customers(customer_name, customer_type, email, phone)`).eq('company_id', profile.company_id).order('created_at', { ascending: false }),
        supabase.from('customers').select('*').eq('company_id', profile.company_id).order('customer_name'),
        supabase.from('companies').select('*').eq('id', profile.company_id).single(),
      ])
      setQuotes(q || [])
      setCustomers(c || [])
      setCompany(co)
      if (co) {
        setForm(f => ({
          ...f,
          payment_terms: co.payment_terms || 'Payment due within 30 days',
          terms_conditions: co.terms_conditions || '',
        }))
      }
    }
    setLoading(false)
  }

  const resetForm = () => {
    setForm({
      customer_id: '', reference: '', valid_until: '', notes: '',
      payment_terms: company?.payment_terms || 'Payment due within 30 days',
      terms_conditions: company?.terms_conditions || '',
      vat_enabled: true, vat_rate: 20, items: defaultItems,
    })
    setError(null)
  }

  const updateItem = (index, field, value) => {
    const items = [...form.items]
    items[index] = { ...items[index], [field]: value }
    if (field === 'quantity' || field === 'unit_price') {
      items[index].total = parseFloat(items[index].quantity || 0) * parseFloat(items[index].unit_price || 0)
    }
    setForm({ ...form, items })
  }

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, total: 0 }] })
  }

  const removeItem = (index) => {
    if (form.items.length === 1) return
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) })
  }

  const subtotal = form.items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
  const vatAmount = form.vat_enabled ? subtotal * (parseFloat(form.vat_rate) / 100) : 0
  const grandTotal = subtotal + vatAmount

  const handleCreate = async () => {
    if (!form.customer_id) { setError('Please select a customer.'); return }
    if (form.items.some(i => !i.description.trim())) { setError('All line items need a description.'); return }
    setSaving(true)
    setError(null)

    const { data: co } = await supabase.from('companies').select('next_quote_number').eq('id', companyId).single()
    const quoteNumber = co.next_quote_number

    const { data: newQuote, error: insertError } = await supabase.from('quotes').insert([{
      company_id: companyId,
      customer_id: form.customer_id,
      quote_number_numeric: quoteNumber,
      status: 'Draft',
      reference: form.reference.trim(),
      valid_until: form.valid_until || null,
      notes: form.notes.trim(),
      payment_terms: form.payment_terms.trim(),
      terms_conditions: form.terms_conditions.trim(),
      vat_enabled: form.vat_enabled,
      vat_rate: parseFloat(form.vat_rate),
      subtotal, vat_amount: vatAmount, total: grandTotal,
    }]).select().single()

    if (insertError) { setError(insertError.message); setSaving(false); return }

    await supabase.from('quote_items').insert(
      form.items.map(item => ({
        quote_id: newQuote.id,
        description: item.description,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        total: parseFloat(item.total),
      }))
    )

    await supabase.from('companies').update({ next_quote_number: quoteNumber + 1 }).eq('id', companyId)
    setShowAddModal(false)
    resetForm()
    fetchData()
    setSaving(false)
  }

  const handleStatusChange = async (quoteId, newStatus) => {
    await supabase.from('quotes').update({ status: newStatus }).eq('id', quoteId)
    fetchData()
  }

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return
    await supabase.from('quotes').delete().eq('id', quoteId)
    fetchData()
  }

  const filteredQuotes = quotes
    .filter(q => {
      const matchesStatus = statusFilter === 'All' || q.status === statusFilter
      const matchesSearch = search === '' ||
        q.customers?.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        `Q-${String(q.quote_number_numeric).padStart(3, '0')}`.toLowerCase().includes(search.toLowerCase())
      return matchesStatus && matchesSearch
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
      if (sortBy === 'highest') return parseFloat(b.total || 0) - parseFloat(a.total || 0)
      if (sortBy === 'lowest') return parseFloat(a.total || 0) - parseFloat(b.total || 0)
      return 0
    })

  const statusStyle = (status) => {
    const map = {
      Draft:    { background: '#F1F5F9', color: '#64748B' },
      Sent:     { background: '#EFF6FF', color: '#2563EB' },
      Accepted: { background: '#F0FDF4', color: '#15803D' },
      Rejected: { background: '#FFF1F2', color: '#BE123C' },
    }
    return {
      ...(map[status] || map.Draft),
      padding: '3px 10px', borderRadius: '999px',
      fontSize: '12px', fontWeight: '600',
      display: 'inline-block', whiteSpace: 'nowrap',
    }
  }

  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    background: '#FAFAFA', fontFamily: 'Inter, sans-serif',
  }

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  }

  const selectStyle = {
    height: '40px', borderRadius: '10px', padding: '0 14px',
    border: '1.5px solid #E2E8F0', fontSize: '13px',
    outline: 'none', background: 'white', cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', color: '#374151',
  }

  const btnStyle = {
    padding: '5px 0', borderRadius: '6px', fontSize: '11px',
    fontWeight: '600', cursor: 'pointer', width: '56px',
    textAlign: 'center',
  }

  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />
      

      <div style={{ marginLeft: '240px', flex: 1, padding: '32px 32px 32px 32px', minWidth: 0 }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: '20px',
        }}>
          <div>
            <h1 style={{
              fontSize: '22px', fontWeight: '800', color: '#0F172A',
              margin: '0 0 4px', letterSpacing: '-0.4px',
            }}>Quotes</h1>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
              {filteredQuotes.length} of {quotes.length} quotes
            </p>
          </div>
          <button onClick={() => { resetForm(); setShowAddModal(true) }} style={{
            height: '40px', padding: '0 20px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
            color: 'white', fontSize: '13px', fontWeight: '700',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(37,99,235,0.30)',
          }}>+ New Quote</button>
        </div>

        <TrialBanner session={session} />

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by customer or quote number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, height: '40px', borderRadius: '10px',
              border: '1.5px solid #E2E8F0', padding: '0 14px',
              fontSize: '13px', outline: 'none', background: 'white',
              fontFamily: 'Inter, sans-serif',
            }}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...selectStyle, minWidth: '140px' }}>
            <option value="All">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Accepted">Accepted</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ ...selectStyle, minWidth: '150px' }}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Value</option>
            <option value="lowest">Lowest Value</option>
          </select>
        </div>

        {/* Table */}
        <div style={{
          background: 'white', borderRadius: '12px',
          border: '1px solid #EAECF0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          width: '100%', overflowX: 'auto',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 100px 100px 110px 280px',
            padding: '10px 20px', background: '#F8FAFC',
            borderBottom: '1px solid #EAECF0',
            minWidth: '800px',
          }}>
            {['Quote #', 'Customer', 'Total', 'Status', 'Date', 'Actions'].map((h, i) => (
              <span key={i} style={{
                fontSize: '11px', fontWeight: '600', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{h}</span>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
          ) : filteredQuotes.length === 0 ? (
            <div style={{ padding: '64px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: '0 0 6px' }}>
                {quotes.length === 0 ? 'No quotes yet' : 'No quotes match your filters'}
              </p>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 20px' }}>
                {quotes.length === 0
                  ? customers.length === 0 ? 'Add a customer first' : 'Click "+ New Quote" to get started'
                  : 'Try changing your search or filters'}
              </p>
              {quotes.length === 0 && (
                <button onClick={() => { resetForm(); setShowAddModal(true) }} style={{
                  padding: '8px 20px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                  color: 'white', fontSize: '13px', fontWeight: '600',
                  border: 'none', cursor: 'pointer',
                }}>+ New Quote</button>
              )}
            </div>
          ) : (
            filteredQuotes.map((quote, i) => (
              <div key={quote.id} style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr 100px 100px 110px 280px',
                padding: '14px 20px', alignItems: 'center',
                borderBottom: i < filteredQuotes.length - 1 ? '1px solid #F1F5F9' : 'none',
                minWidth: '800px',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFBFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB' }}>
                  Q-{String(quote.quote_number_numeric).padStart(3, '0')}
                </span>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A', margin: '0 0 2px' }}>
                    {quote.customers?.customer_name || '—'}
                  </p>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                    {quote.customers?.customer_type || ''}
                  </span>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>
                  £{parseFloat(quote.total || 0).toFixed(2)}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={statusStyle(quote.status)}>{quote.status}</span>
                </div>
                <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                  {new Date(quote.created_at).toLocaleDateString('en-GB')}
                </span>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button onClick={() => navigate(`/quotes/${quote.id}`)} style={{
                    ...btnStyle, border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#374151',
                  }}>View</button>
                  {quote.status === 'Draft' && (
                    <button onClick={() => handleStatusChange(quote.id, 'Sent')} style={{
                      ...btnStyle, border: 'none',
                      background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                      color: 'white',
                    }}>Send</button>
                  )}
                  {quote.status === 'Sent' && (
                    <button onClick={() => handleStatusChange(quote.id, 'Accepted')} style={{
                      ...btnStyle, border: 'none',
                      background: '#15803D', color: 'white',
                    }}>Accept</button>
                  )}
                  {(quote.status === 'Draft' || quote.status === 'Sent') && (
                    <button onClick={() => handleStatusChange(quote.id, 'Rejected')} style={{
                      ...btnStyle, border: '1.5px solid #FECDD3',
                      background: 'white', color: '#BE123C',
                    }}>Reject</button>
                  )}
                  <button onClick={() => handleDelete(quote.id)} style={{
                    ...btnStyle, border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#94A3B8',
                  }}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* New Quote Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '700px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '24px',
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                New Quote
              </h2>
              <div onClick={() => { setShowAddModal(false); resetForm() }} style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: '#F1F5F9', display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer',
                fontSize: '16px', color: '#64748B', fontWeight: '600',
              }}>x</div>
            </div>

            {error && (
              <div style={{
                background: '#FFF1F2', border: '1px solid #FECDD3',
                borderRadius: '8px', padding: '12px',
                color: '#BE123C', fontSize: '13px', marginBottom: '16px',
              }}>{error}</div>
            )}

            {customers.length === 0 ? (
              <div style={{
                background: '#FFF8E1', border: '1px solid #FDE68A',
                borderRadius: '8px', padding: '16px',
                color: '#92400E', fontSize: '13px', textAlign: 'center',
              }}>
                You need to add a customer first before creating a quote.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>Customer *</label>
                    <select value={form.customer_id}
                      onChange={e => setForm({ ...form, customer_id: e.target.value })}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Select customer...</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.customer_name} ({c.customer_type || 'Business'})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Reference / PO Number</label>
                    <input type="text" value={form.reference}
                      onChange={e => setForm({ ...form, reference: e.target.value })}
                      placeholder="e.g. PO-12345" style={inputStyle} />
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Valid Until</label>
                  <input type="date" value={form.valid_until}
                    onChange={e => setForm({ ...form, valid_until: e.target.value })}
                    style={{ ...inputStyle, width: '50%' }} />
                </div>

                {/* Line items */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                    <button onClick={addItem} style={{
                      padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: '600', border: '1.5px solid #2563EB',
                      background: 'white', color: '#2563EB', cursor: 'pointer',
                    }}>+ Add Item</button>
                  </div>

                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px',
                    gap: '8px', marginBottom: '6px', padding: '0 4px',
                  }}>
                    {['Description', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
                      <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
                    ))}
                  </div>

                  {form.items.map((item, index) => (
                    <div key={index} style={{
                      display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px',
                      gap: '8px', marginBottom: '8px', alignItems: 'center',
                    }}>
                      <input type="text" value={item.description}
                        onChange={e => updateItem(index, 'description', e.target.value)}
                        placeholder="e.g. Labour"
                        style={{ ...inputStyle, height: '40px' }} />
                      <input type="number" value={item.quantity}
                        onChange={e => updateItem(index, 'quantity', e.target.value)}
                        min="0" style={{ ...inputStyle, height: '40px' }} />
                      <input type="number" value={item.unit_price}
                        onChange={e => updateItem(index, 'unit_price', e.target.value)}
                        min="0" placeholder="0.00"
                        style={{ ...inputStyle, height: '40px' }} />
                      <div style={{
                        height: '40px', borderRadius: '10px',
                        background: '#F8FAFC', border: '1.5px solid #E2E8F0',
                        display: 'flex', alignItems: 'center',
                        padding: '0 14px', fontSize: '14px',
                        fontWeight: '600', color: '#0F172A',
                      }}>
                        £{parseFloat(item.total || 0).toFixed(2)}
                      </div>
                      <button onClick={() => removeItem(index)}
                        disabled={form.items.length === 1}
                        style={{
                          width: '32px', height: '32px', borderRadius: '6px',
                          border: '1.5px solid #FECDD3', background: 'white',
                          color: '#BE123C', cursor: form.items.length === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '16px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', opacity: form.items.length === 1 ? 0.3 : 1,
                        }}>×</button>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{
                  background: '#F8FAFC', borderRadius: '10px',
                  border: '1px solid #E2E8F0', padding: '16px', marginBottom: '16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ width: '280px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#64748B' }}>Subtotal</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{subtotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div onClick={() => setForm({ ...form, vat_enabled: !form.vat_enabled })} style={{
                            width: '18px', height: '18px', borderRadius: '4px',
                            border: `2px solid ${form.vat_enabled ? '#2563EB' : '#E2E8F0'}`,
                            background: form.vat_enabled ? '#2563EB' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          }}>
                            {form.vat_enabled && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: '13px', color: '#64748B' }}>VAT</span>
                          {form.vat_enabled && (
                            <>
                              <input type="number" value={form.vat_rate}
                                onChange={e => setForm({ ...form, vat_rate: e.target.value })}
                                style={{ width: '50px', height: '28px', borderRadius: '6px', border: '1.5px solid #E2E8F0', padding: '0 8px', fontSize: '13px', outline: 'none', background: 'white' }} />
                              <span style={{ fontSize: '13px', color: '#64748B' }}>%</span>
                            </>
                          )}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{vatAmount.toFixed(2)}</span>
                      </div>
                      <div style={{ height: '1px', background: '#E2E8F0', margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>Total</span>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563EB' }}>£{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Payment Terms</label>
                  <input type="text" value={form.payment_terms}
                    onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                    placeholder="e.g. Payment due within 30 days" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Terms & Conditions</label>
                  <textarea value={form.terms_conditions} onChange={e => setForm({ ...form, terms_conditions: e.target.value })}
                    placeholder="Your terms and conditions..."
                    style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setShowAddModal(false); resetForm() }} style={{
                    flex: 1, height: '44px', borderRadius: '10px',
                    background: 'white', border: '1.5px solid #E2E8F0',
                    fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
                  }}>Cancel</button>
                  <button onClick={handleCreate} disabled={saving} style={{
                    flex: 1, height: '44px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                    color: 'white', fontSize: '14px', fontWeight: '700',
                    border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                  }}>{saving ? 'Creating...' : 'Create Quote'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}